import { WidgetConfig } from "./remote_config";
import { ChatMessage } from "./session";

/**
 * Chrome strings the widget renders itself (everything else — greeting,
 * chips, intake questions — arrives pre-localized from the server).
 * Keyed by the boot-time locale; nl is the platform default.
 */
export interface UiStrings {
    placeholder: string;
    /** The input's label for screen readers (the placeholder is no
     *  label). */
    inputLabel: string;
    confirm: string;
    skip: string;
    send: string;
    sendFailed: string;
    startFailed: string;
    openChat: string;
    closeChat: string;
    /** The × of a page's chat window while it fills a phone screen. */
    exitFullScreen: string;
    /** Header line under the bot name, when the agency didn't set
     *  one — discloses that the visitor is talking to a bot. */
    aiAssistant: string;
    /** Footer credit, followed by the maker's logo. */
    madeBy: string;
    /** The × on the teaser bubble next to the closed launcher. */
    dismissTeaser: string;
}

const UI_STRINGS: Record<string, UiStrings> = {
    nl: {
        placeholder: "Typ een bericht…",
        inputLabel: "Bericht",
        confirm: "Bevestigen",
        skip: "Overslaan",
        send: "Verzenden",
        sendFailed: "Bericht niet verzonden. Probeer het opnieuw.",
        startFailed:
            "De chat kon niet starten. Sluit en open de chat om het opnieuw te proberen.",
        openChat: "Chat openen",
        closeChat: "Chat sluiten",
        exitFullScreen: "Volledig scherm sluiten",
        aiAssistant: "AI-assistent",
        madeBy: "Gemaakt door",
        dismissTeaser: "Bericht sluiten",
    },
    fr: {
        placeholder: "Écrivez un message…",
        inputLabel: "Message",
        confirm: "Confirmer",
        skip: "Passer",
        send: "Envoyer",
        sendFailed: "Message non envoyé. Veuillez réessayer.",
        startFailed:
            "Le chat n'a pas pu démarrer. Fermez et rouvrez le chat pour réessayer.",
        openChat: "Ouvrir le chat",
        closeChat: "Fermer le chat",
        exitFullScreen: "Quitter le plein écran",
        aiAssistant: "Assistant IA",
        madeBy: "Réalisé par",
        dismissTeaser: "Fermer le message",
    },
    en: {
        placeholder: "Type a message…",
        inputLabel: "Message",
        confirm: "Confirm",
        skip: "Skip",
        send: "Send",
        sendFailed: "Message not sent. Please try again.",
        startFailed:
            "The chat could not start. Close and reopen the chat to try again.",
        openChat: "Open chat",
        closeChat: "Close chat",
        exitFullScreen: "Exit full screen",
        aiAssistant: "AI assistant",
        madeBy: "Made by",
        dismissTeaser: "Dismiss message",
    },
};

/** The chrome strings for a locale; nl when the widget doesn't know it. */
export function uiStrings(locale: string): UiStrings {
    return UI_STRINGS[locale] ?? UI_STRINGS.nl;
}

/** A suggested answer: a welcome quick reply or a chip under a bot
 *  message. `value` is what gets sent, `title` what the button says. */
export interface QuickReply {
    title: string;
    value: string;
}

/**
 * One chat window: header, scrollable message list, the welcome quick
 * replies, the input row and the maker credit. The floating bubble's
 * popover is one (launcher.ts), a window placed in the page is another
 * (embed.ts). It only draws: what the visitor sends goes to `onSend`, and
 * what to show comes from the page's one Conversation (conversation.ts),
 * so every window on the page shows the same chat.
 */
export interface ChatPane {
    /** The visitor's own text field under the messages. */
    readonly inputField: HTMLInputElement;
    /** Shows one message, with the chips, cards or typed input it
     *  carries. `focus`: an input it carries may take the focus. */
    show: (msg: ChatMessage, focus: boolean) => void;
    setTyping: (on: boolean) => void;
    /** Replaces the welcome quick replies above the input row. */
    renderOptions: (opts: QuickReply[]) => void;
    /** The visitor answered message `messageId` (in this window or
     *  another): its chips and typed input stop taking answers. */
    answered: (messageId: string) => void;
    /** Call when the window is on screen again: a list that grew while
     *  it was hidden scrolls to its newest message. */
    revealed: () => void;
    /** Whether a screen reader reads out new messages here. With more
     *  than one window on the page, only the visitor's own one does. */
    setLive: (on: boolean) => void;
    galleryOpen: () => boolean;
    closeGallery: () => void;
}

export interface ChatPaneParams {
    /** The element the window is drawn in (popover or page box). The
     *  photo gallery covers it. */
    frame: HTMLElement;
    config: WidgetConfig;
    ui: UiStrings;
    /** nl / fr / en — used as the `language` param on Places
     *  autocomplete calls so street name variants come back in the
     *  visitor's language. */
    locale: string;
    /** Base URL of the services API — passed through so the inline
     *  address-autocomplete dropdown can hit /v1/api/chat/places/*. */
    baseUrl: string;
    widgetToken: string;
    /** aria-label of the × in the header. */
    closeLabel: string;
    onClose: () => void;
    /** The visitor sent `text` from this window: typed, a quick reply,
     *  a chip or a typed inline answer. `answering` is the id of the bot
     *  message whose chips or input it came from. */
    onSend: (text: string, answering?: string) => void;
    /** id for the bot name in the header, so the window can be labelled
     *  by it. */
    titleId?: string;
    /** Whether an inline input that arrives may take the focus right
     *  now. Default: always, as in the bubble. */
    mayFocus?: () => boolean;
    focusOptions?: FocusOptions;
}

/** Numbers the windows on a page, for their element ids. */
let paneCount = 0;

export function buildChatPane(params: ChatPaneParams): ChatPane {
    const { frame, config, ui, locale, baseUrl, widgetToken } = params;
    const paneId = ++paneCount;

    // Header identity block: avatar, bot name, and a smaller line
    // disclosing that this is an AI. The agency can override the wording
    // (chatbot.widget_subtitle); we drop it when it just repeats the bot
    // name, which is what a default install would otherwise show.
    const subtitle = (config.subtitle || "").trim() || ui.aiAssistant;
    const showSubtitle =
        subtitle.toLowerCase() !== (config.bot_name || "").trim().toLowerCase();
    const header = document.createElement("div");
    header.className = "amai-chat-header";
    header.innerHTML = `
        <div class="amai-chat-header-id">
            <div class="amai-chat-header-avatar"></div>
            <div class="amai-chat-header-text">
                <div class="amai-chat-header-title"${params.titleId ? ` id="${escapeHtml(params.titleId)}"` : ""}>${escapeHtml(config.bot_name)}</div>
                ${showSubtitle ? `<div class="amai-chat-header-subtitle">${escapeHtml(subtitle)}</div>` : ""}
            </div>
        </div>
        <button class="amai-chat-close" aria-label="${escapeHtml(params.closeLabel)}">×</button>
    `;
    frame.appendChild(header);
    header
        .querySelector(".amai-chat-close")
        ?.addEventListener("click", params.onClose);

    // The agency's bubble artwork doubles as the header avatar. Same
    // onerror guard as the launcher: a broken URL must not leave a
    // blank square, so we fall back to the built-in chat glyph.
    const avatarSlot = header.querySelector<HTMLDivElement>(
        ".amai-chat-header-avatar",
    )!;
    if (config.bubble_image_url) {
        const avatarImg = document.createElement("img");
        avatarImg.src = config.bubble_image_url;
        avatarImg.alt = "";
        avatarImg.onerror = (): void => {
            avatarImg.remove();
            avatarSlot.innerHTML = CHAT_ICON;
        };
        avatarSlot.appendChild(avatarImg);
    } else {
        avatarSlot.innerHTML = CHAT_ICON;
    }

    // A log: screen readers read out each new message as it arrives,
    // without moving the visitor away from what they were doing.
    const messages = document.createElement("div");
    messages.className = "amai-chat-messages";
    messages.setAttribute("role", "log");
    messages.setAttribute("aria-live", "polite");
    frame.appendChild(messages);

    const optionsRow = document.createElement("div");
    optionsRow.className = "amai-chat-options";
    frame.appendChild(optionsRow);

    const inputRow = document.createElement("form");
    inputRow.className = "amai-chat-input";
    const inputId = `amai-chat-input-${paneId}`;
    inputRow.innerHTML = `
        <label class="amai-chat-sr-only" for="${inputId}">${escapeHtml(ui.inputLabel)}</label>
        <input type="text" id="${inputId}" class="amai-chat-input-field" placeholder="${escapeHtml(ui.placeholder)}" autocomplete="off" />
        <button type="submit" class="amai-chat-send" aria-label="${escapeHtml(ui.send)}">${SEND_ICON}</button>
    `;
    frame.appendChild(inputRow);

    // "Gemaakt door <maker logo>" credit. The maker is the platform
    // brand (amai.immo / buitenaards.be), not the agency — it comes
    // from the server's BRAND env, so one widget build serves both.
    // Omitted entirely when talking to a services instance that predates
    // the `maker` field.
    // http(s) only, matching the guard on property links: an href is
    // the one config value that would execute if it ever arrived as
    // `javascript:`, so it does not get to bypass the same check just
    // because this one comes from our own brand table.
    if (config.maker?.name && /^https?:\/\//i.test(config.maker?.url ?? "")) {
        const footer = document.createElement("div");
        footer.className = "amai-chat-footer";

        const credit = document.createElement("a");
        credit.className = "amai-chat-footer-credit";
        credit.href = config.maker.url;
        credit.target = "_blank";
        credit.rel = "noopener noreferrer";
        credit.title = `${ui.madeBy} ${config.maker.name}`;

        const label = document.createElement("span");
        label.textContent = ui.madeBy;
        credit.appendChild(label);

        const nameEl = document.createElement("span");
        nameEl.className = "amai-chat-footer-name";
        nameEl.textContent = config.maker.name;
        credit.appendChild(nameEl);

        footer.appendChild(credit);
        frame.appendChild(footer);
    }

    const inputField = inputRow.querySelector<HTMLInputElement>(".amai-chat-input-field")!;
    let scrollPending = false;
    /** Per bot message: the chip rows and typed inputs it brought. */
    const answerControls = new Map<string, HTMLElement[]>();

    function renderOptions(opts: QuickReply[]): void {
        optionsRow.innerHTML = "";
        for (const opt of opts) {
            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = "amai-chat-option";
            btn.textContent = opt.title;
            btn.addEventListener("click", (e) => {
                // Sent first, while the suggestions are still there: a
                // compact page window grows from the size it has on
                // screen (embed.ts).
                params.onSend(opt.value);
                // Picked with the keyboard (a click without a pointer):
                // the focus is on this button, which goes now. The field
                // for the next message takes it, not the page.
                const fromKeyboard = e.detail === 0;
                optionsRow.innerHTML = "";
                if (fromKeyboard && (params.mayFocus?.() ?? true)) {
                    inputField.focus(params.focusOptions);
                }
            });
            optionsRow.appendChild(btn);
        }
    }

    let activeGallery: HTMLDivElement | undefined;

    function openGallery(images: { url: string }[], startIndex: number): void {
        if (images.length === 0) return;
        closeGallery();
        let idx = Math.max(0, Math.min(startIndex, images.length - 1));

        const overlay = document.createElement("div");
        overlay.className = "amai-chat-gallery";
        overlay.setAttribute("role", "dialog");
        overlay.setAttribute("aria-modal", "true");

        const wrap = document.createElement("div");
        wrap.className = "amai-chat-gallery-image-wrap";
        const img = document.createElement("img");
        img.className = "amai-chat-gallery-image";
        img.alt = "";
        wrap.appendChild(img);
        overlay.appendChild(wrap);

        const counter = document.createElement("div");
        counter.className = "amai-chat-gallery-counter";
        overlay.appendChild(counter);

        const close = document.createElement("button");
        close.type = "button";
        close.className = "amai-chat-gallery-close";
        close.setAttribute("aria-label", "Close gallery");
        close.textContent = "×";
        close.addEventListener("click", (e) => {
            e.stopPropagation();
            closeGallery();
        });
        overlay.appendChild(close);

        let prev: HTMLButtonElement | undefined;
        let next: HTMLButtonElement | undefined;
        if (images.length > 1) {
            prev = document.createElement("button");
            prev.type = "button";
            prev.className = "amai-chat-gallery-prev";
            prev.setAttribute("aria-label", "Previous image");
            prev.textContent = "‹";
            prev.addEventListener("click", (e) => {
                e.stopPropagation();
                idx = (idx - 1 + images.length) % images.length;
                refresh();
            });
            overlay.appendChild(prev);

            next = document.createElement("button");
            next.type = "button";
            next.className = "amai-chat-gallery-next";
            next.setAttribute("aria-label", "Next image");
            next.textContent = "›";
            next.addEventListener("click", (e) => {
                e.stopPropagation();
                idx = (idx + 1) % images.length;
                refresh();
            });
            overlay.appendChild(next);
        }

        const refresh = (): void => {
            img.src = images[idx].url;
            counter.textContent = `${idx + 1} / ${images.length}`;
        };
        refresh();

        // Click outside the image closes; image clicks themselves do
        // nothing (so the visitor can long-press / right-click to save).
        overlay.addEventListener("click", (e) => {
            if (e.target === overlay || e.target === wrap || e.target === counter) {
                closeGallery();
            }
        });

        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                closeGallery();
            } else if (e.key === "ArrowLeft" && prev) {
                idx = (idx - 1 + images.length) % images.length;
                refresh();
            } else if (e.key === "ArrowRight" && next) {
                idx = (idx + 1) % images.length;
                refresh();
            }
        };
        document.addEventListener("keydown", onKey);
        (overlay as unknown as { _onKey: (e: KeyboardEvent) => void })._onKey =
            onKey;

        frame.appendChild(overlay);
        activeGallery = overlay;
    }

    function closeGallery(): void {
        if (!activeGallery) return;
        const onKey = (
            activeGallery as unknown as { _onKey?: (e: KeyboardEvent) => void }
        )._onKey;
        if (onKey) document.removeEventListener("keydown", onKey);
        activeGallery.remove();
        activeGallery = undefined;
    }

    function renderPropertyCards(cards: PropertyCard[]): void {
        const carousel = document.createElement("div");
        carousel.className = "amai-chat-property-cards";
        for (const card of cards) {
            carousel.appendChild(buildPropertyCardElement(card));
        }
        messages.appendChild(carousel);
    }

    function buildPropertyCardElement(card: PropertyCard): HTMLAnchorElement {
        const el = document.createElement("a");
        el.className = "amai-chat-property-card";
        el.href = card.url;
        el.target = "_blank";
        el.rel = "noopener noreferrer";

        if (card.images.length > 0) {
            // Thumbnail row: up to 4 small rounded tiles. If the
            // property has more than 4 images, the last tile becomes a
            // "+N more" overlay that still opens the gallery — visitor
            // can browse the rest from there. Click any tile → gallery.
            const MAX_THUMBS = 4;
            const totalImages = card.images.length;
            const moreCount =
                totalImages > MAX_THUMBS ? totalImages - (MAX_THUMBS - 1) : 0;
            const visibleAsThumbs = moreCount > 0 ? MAX_THUMBS - 1 : totalImages;

            const row = document.createElement("div");
            row.className = "amai-chat-property-card-thumbs";

            for (let i = 0; i < visibleAsThumbs; i++) {
                row.appendChild(
                    buildThumbnail(card.images[i].url, i, card.images),
                );
            }

            if (moreCount > 0) {
                const moreIndex = visibleAsThumbs;
                const more = buildThumbnail(
                    card.images[moreIndex].url,
                    moreIndex,
                    card.images,
                );
                more.classList.add("amai-chat-property-card-thumb-more");
                const overlay = document.createElement("span");
                overlay.className = "amai-chat-property-card-thumb-more-label";
                overlay.textContent = `+${moreCount}`;
                more.appendChild(overlay);
                row.appendChild(more);
            }

            el.appendChild(row);
        }

        const body = document.createElement("div");
        body.className = "amai-chat-property-card-body";

        const title = document.createElement("div");
        title.className = "amai-chat-property-card-title";
        title.textContent = card.label || card.name || "Property";
        body.appendChild(title);

        const meta = document.createElement("div");
        meta.className = "amai-chat-property-card-meta";
        const parts: string[] = [];
        if (card.price) parts.push(formatCardPrice(card.price));
        if (typeof card.bedrooms === "number")
            parts.push(`${card.bedrooms} 🛏`);
        if (typeof card.bathrooms === "number")
            parts.push(`${card.bathrooms} 🛁`);
        if (typeof card.area_m2 === "number")
            parts.push(`${Math.round(card.area_m2)} m²`);
        meta.textContent = parts.join(" · ");
        if (parts.length > 0) body.appendChild(meta);

        const locParts: string[] = [];
        if (card.postcode) locParts.push(card.postcode);
        if (card.city) locParts.push(card.city);
        if (locParts.length > 0) {
            const loc = document.createElement("div");
            loc.className = "amai-chat-property-card-location";
            loc.textContent = locParts.join(" ");
            body.appendChild(loc);
        }

        const cta = document.createElement("div");
        cta.className = "amai-chat-property-card-cta";
        cta.textContent = cardCtaLabel(locale);
        body.appendChild(cta);

        el.appendChild(body);
        return el;
    }

    function buildThumbnail(
        url: string,
        index: number,
        allImages: { url: string }[],
    ): HTMLButtonElement {
        const t = document.createElement("button");
        t.type = "button";
        t.className = "amai-chat-property-card-thumb";
        t.setAttribute("aria-label", "Open image gallery");
        const img = document.createElement("img");
        img.src = url;
        img.alt = "";
        img.loading = "lazy";
        img.decoding = "async";
        t.appendChild(img);
        t.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            openGallery(allImages, index);
        });
        return t;
    }

    function cardCtaLabel(loc: string): string {
        const head = loc.slice(0, 2).toLowerCase();
        if (head === "nl") return "Bekijk woning ↗";
        if (head === "fr") return "Voir le bien ↗";
        return "View property ↗";
    }

    function formatCardPrice(price: { amount: number; currency: string }): string {
        const symbol = price.currency === "EUR" ? "€" : `${price.currency} `;
        // Belgian thousands separator is a dot.
        const formatted = Math.round(price.amount)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        return `${symbol}${formatted}`;
    }

    function renderPropertyLinks(
        links: { title: string; url: string }[],
    ): void {
        const row = document.createElement("div");
        row.className = "amai-chat-property-links";
        for (const link of links) {
            const a = document.createElement("a");
            a.className = "amai-chat-property-link";
            a.href = link.url;
            a.target = "_blank";
            a.rel = "noopener noreferrer";
            a.textContent = link.title;
            row.appendChild(a);
        }
        messages.appendChild(row);
    }

    function renderSingleChips(opts: QuickReply[], messageId: string): void {
        const row = document.createElement("div");
        row.className = "amai-chat-inline-options";
        for (const opt of opts) {
            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = "amai-chat-option";
            btn.textContent = opt.title;
            btn.addEventListener("click", () => {
                row.querySelectorAll("button").forEach((b) => {
                    (b as HTMLButtonElement).disabled = true;
                });
                params.onSend(opt.value, messageId);
            });
            row.appendChild(btn);
        }
        messages.appendChild(row);
        rememberAnswerControls(messageId, row);
    }

    function renderMultiChips(
        opts: QuickReply[],
        meta: OptionsMeta,
        messageId: string,
    ): void {
        // Two stacked rows for multi-select: the chip grid on top, the
        // submit button on its own row beneath. Keeps the visual hierarchy
        // obvious ("pick some, then tap Confirm") and prevents the
        // Confirm button from wrapping into the chip flow.
        const block = document.createElement("div");
        block.className = "amai-chat-inline-multi";

        const chipsRow = document.createElement("div");
        chipsRow.className = "amai-chat-inline-options amai-chat-inline-options-multi";
        block.appendChild(chipsRow);

        const actionRow = document.createElement("div");
        actionRow.className = "amai-chat-inline-multi-actions";
        block.appendChild(actionRow);

        const submitBtn = document.createElement("button");
        submitBtn.type = "button";
        submitBtn.className = "amai-chat-multi-submit";
        submitBtn.textContent = meta.confirmLabel || ui.confirm;
        submitBtn.disabled = true;
        actionRow.appendChild(submitBtn);

        const selected = new Set<string>();
        const exclusiveValues = new Set(meta.exclusiveValues ?? []);
        const chipButtons: HTMLButtonElement[] = [];

        const syncVisualState = (): void => {
            for (const b of chipButtons) {
                const isOn = selected.has(b.dataset.value || "");
                b.classList.toggle("amai-chat-option-selected", isOn);
                b.setAttribute("aria-pressed", isOn ? "true" : "false");
            }
            submitBtn.disabled = selected.size === 0;
        };

        for (const opt of opts) {
            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = "amai-chat-option amai-chat-option-toggle";
            btn.dataset.value = opt.value;
            btn.textContent = opt.title;
            btn.setAttribute("aria-pressed", "false");
            btn.addEventListener("click", () => {
                if (selected.has(opt.value)) {
                    selected.delete(opt.value);
                } else {
                    if (exclusiveValues.has(opt.value)) {
                        selected.clear();
                    } else {
                        for (const ex of exclusiveValues) {
                            selected.delete(ex);
                        }
                    }
                    selected.add(opt.value);
                }
                syncVisualState();
            });
            chipsRow.appendChild(btn);
            chipButtons.push(btn);
        }

        submitBtn.addEventListener("click", () => {
            if (selected.size === 0) return;
            for (const b of chipButtons) b.disabled = true;
            submitBtn.disabled = true;
            const payload = opts
                .filter((o) => selected.has(o.value))
                .map((o) => o.value)
                .join(", ");
            params.onSend(payload, messageId);
        });

        messages.appendChild(block);
        rememberAnswerControls(messageId, block);
    }

    function renderInlineInput(
        inputSpec: InlineInputSpec,
        messageId: string,
        focus: boolean,
    ): void {
        const form = document.createElement("form");
        form.className = "amai-chat-inline-input";

        const field = document.createElement("input");
        field.className = "amai-chat-inline-input-field";
        field.type =
            inputSpec.type === "year"
                ? "number"
                : inputSpec.type === "email"
                  ? "email"
                  : inputSpec.type === "tel"
                    ? "tel"
                    : inputSpec.type === "number"
                      ? "number"
                      : "text";
        if (inputSpec.placeholder) field.placeholder = inputSpec.placeholder;
        if (typeof inputSpec.min === "number") field.min = String(inputSpec.min);
        if (typeof inputSpec.max === "number") field.max = String(inputSpec.max);
        if (inputSpec.type === "year") field.step = "1";
        field.required = true;
        field.autocomplete = "off";

        const submit = document.createElement("button");
        submit.type = "submit";
        submit.className = "amai-chat-inline-input-send";
        submit.innerHTML = SEND_ICON;
        submit.setAttribute("aria-label", ui.send);

        form.appendChild(field);
        form.appendChild(submit);

        // Secondary "Skip" button for the few typed inputs where the
        // visitor may genuinely not know (area m², year built). Sends
        // the literal `unknown` so the estimate prompt handles it
        // identically to the chips' "Weet niet" / "Je ne sais pas" /
        // "Don't know" option.
        let skipBtn: HTMLButtonElement | undefined;
        if (inputSpec.skippable) {
            skipBtn = document.createElement("button");
            skipBtn.type = "button";
            skipBtn.className = "amai-chat-inline-input-skip";
            skipBtn.textContent = inputSpec.skip_label || ui.skip;
            skipBtn.addEventListener("click", () => {
                field.disabled = true;
                submit.disabled = true;
                if (skipBtn) skipBtn.disabled = true;
                params.onSend(inputSpec.skip_value || "unknown", messageId);
            });
            form.appendChild(skipBtn);
        }

        messages.appendChild(form);
        rememberAnswerControls(messageId, form);

        if (inputSpec.autocomplete === "be-address") {
            attachAddressAutocomplete(form, field, submit);
        }

        form.addEventListener("submit", (e) => {
            e.preventDefault();
            const value = field.value.trim();
            if (!value) return;
            field.disabled = true;
            submit.disabled = true;
            if (skipBtn) skipBtn.disabled = true;
            params.onSend(value, messageId);
        });

        if (focus && (params.mayFocus?.() ?? true)) {
            setTimeout(() => field.focus(params.focusOptions), 30);
        }
    }

    function rememberAnswerControls(messageId: string, el: HTMLElement): void {
        const list = answerControls.get(messageId) ?? [];
        list.push(el);
        answerControls.set(messageId, list);
    }

    function answered(messageId: string): void {
        for (const el of answerControls.get(messageId) ?? []) {
            el.querySelectorAll<HTMLButtonElement | HTMLInputElement>(
                "button, input",
            ).forEach((control) => {
                control.disabled = true;
            });
        }
    }

    /**
     * Wire a Google Places (New) dropdown beneath an address input. Calls
     * the services proxy at /v1/api/chat/places/* — the API key stays
     * server-side. Uses a session token so all keystrokes in one address
     * pick are billed as a single Places session (~$0.017) rather than
     * per-keystroke.
     *
     * UX details:
     *   - Arrow keys move through suggestions; Enter picks the highlighted
     *     one (or submits the form if none is highlighted); Escape closes
     *     the dropdown.
     *   - After picking, focus STAYS on the input with the cursor at the
     *     end. If the picked description lacks a house number, we fire
     *     one more autocomplete query so numbered variants surface
     *     without the visitor having to backspace or click elsewhere.
     */
    function attachAddressAutocomplete(
        wrapper: HTMLElement,
        field: HTMLInputElement,
        _submit: HTMLButtonElement,
    ): void {
        const sessionToken = generateSessionToken();
        const list = document.createElement("ul");
        list.className = "amai-chat-place-suggestions";
        list.setAttribute("role", "listbox");
        wrapper.style.position = "relative";
        wrapper.appendChild(list);

        type Prediction = { place_id: string; description: string };
        let predictions: Prediction[] = [];
        let activeIndex = -1;
        let inflight: AbortController | undefined;
        let debounceTimer: number | undefined;
        let lastPickedValue: string | undefined;

        const close = () => {
            list.innerHTML = "";
            list.classList.remove("amai-chat-place-suggestions-open");
            predictions = [];
            activeIndex = -1;
        };

        const hasHouseNumber = (s: string): boolean =>
            /\b\d{1,5}[a-z]?\b/i.test(s);

        const highlight = () => {
            const items = list.querySelectorAll<HTMLLIElement>(
                ".amai-chat-place-suggestion",
            );
            items.forEach((el, i) => {
                if (i === activeIndex) {
                    el.classList.add("amai-chat-place-suggestion-active");
                    el.setAttribute("aria-selected", "true");
                    el.scrollIntoView({ block: "nearest" });
                } else {
                    el.classList.remove("amai-chat-place-suggestion-active");
                    el.setAttribute("aria-selected", "false");
                }
            });
        };

        const select = (p: Prediction) => {
            field.value = p.description;
            lastPickedValue = p.description;
            // Keep focus on the input and move the caret to the end so
            // the visitor can immediately keep typing if the prediction
            // didn't include a house number.
            field.focus();
            const len = field.value.length;
            field.setSelectionRange(len, len);
            if (!hasHouseNumber(p.description)) {
                // No house number in the picked text — refire the
                // autocomplete so Google offers numbered variants for
                // this street. The visitor doesn't have to do anything.
                void fetchSuggestions(field.value);
            } else {
                close();
            }
        };

        const renderSuggestions = (preds: Prediction[]) => {
            predictions = preds.slice(0, 5);
            activeIndex = -1;
            list.innerHTML = "";
            if (predictions.length === 0) {
                list.classList.remove("amai-chat-place-suggestions-open");
                return;
            }
            for (let i = 0; i < predictions.length; i++) {
                const p = predictions[i];
                const li = document.createElement("li");
                li.className = "amai-chat-place-suggestion";
                li.setAttribute("role", "option");
                li.setAttribute("aria-selected", "false");
                li.textContent = p.description;
                li.addEventListener("mousedown", (e) => {
                    // mousedown before blur, so the input stays focused.
                    e.preventDefault();
                    select(p);
                });
                li.addEventListener("mouseenter", () => {
                    activeIndex = i;
                    highlight();
                });
                list.appendChild(li);
            }
            list.classList.add("amai-chat-place-suggestions-open");
        };

        const fetchSuggestions = async (q: string) => {
            inflight?.abort();
            const ctrl = new AbortController();
            inflight = ctrl;
            try {
                const url = new URL(`${baseUrl}/v1/api/chat/places/autocomplete`);
                url.searchParams.set("widget_token", widgetToken);
                url.searchParams.set("input", q);
                url.searchParams.set("session_token", sessionToken);
                url.searchParams.set("language", locale);
                const res = await fetch(url.toString(), { signal: ctrl.signal });
                if (!res.ok) return;
                const body = await res.json();
                const preds = (body?.data?.predictions ?? []) as Prediction[];
                // If the only suggestion equals what the visitor just
                // picked, skip — there's nothing new to offer them.
                if (
                    preds.length === 1 &&
                    preds[0].description === lastPickedValue
                ) {
                    close();
                    return;
                }
                renderSuggestions(preds);
            } catch (err) {
                if ((err as { name?: string })?.name === "AbortError") return;
                console.warn("AmaiChat: places autocomplete failed", err);
            }
        };

        field.addEventListener("input", () => {
            lastPickedValue = undefined;
            const q = field.value.trim();
            if (debounceTimer) clearTimeout(debounceTimer);
            if (q.length < 3) {
                close();
                return;
            }
            debounceTimer = window.setTimeout(() => {
                void fetchSuggestions(q);
            }, 250);
        });

        field.addEventListener("keydown", (e) => {
            const open = list.classList.contains(
                "amai-chat-place-suggestions-open",
            );
            if (e.key === "ArrowDown") {
                if (!open || predictions.length === 0) return;
                e.preventDefault();
                activeIndex = (activeIndex + 1) % predictions.length;
                highlight();
            } else if (e.key === "ArrowUp") {
                if (!open || predictions.length === 0) return;
                e.preventDefault();
                activeIndex =
                    activeIndex <= 0
                        ? predictions.length - 1
                        : activeIndex - 1;
                highlight();
            } else if (e.key === "Enter") {
                if (open && activeIndex >= 0 && activeIndex < predictions.length) {
                    // Picking a suggestion — don't submit the form.
                    e.preventDefault();
                    select(predictions[activeIndex]);
                }
                // Otherwise fall through and let the form's submit
                // handler fire as usual.
            } else if (e.key === "Escape") {
                if (open) {
                    e.preventDefault();
                    close();
                }
            }
        });

        field.addEventListener("blur", () => {
            // Small delay so a click on a suggestion still registers.
            setTimeout(close, 120);
        });
    }

    function show(msg: ChatMessage, focus: boolean): void {
        const bubble = document.createElement("div");
        bubble.className = `amai-chat-bubble amai-chat-bubble-${msg.role}`;
        bubble.innerHTML = renderRichText(msg.content);
        messages.appendChild(bubble);

        if (msg.role !== "visitor") {
            // Quick-reply chips: bot messages can carry an `options` array
            // in content_attributes (set by the brain when the LLM emitted
            // a [CHIPS:a|b|c] marker). Render them as a chip row right
            // below the bubble. Clicking a chip sends its value as a
            // visitor reply and disables the row so the visitor can't
            // double-pick.
            const opts = extractMessageOptions(msg);
            if (opts.length > 0) {
                const meta = extractOptionsMeta(msg);
                if (meta.multi) {
                    renderMultiChips(opts, meta, msg.id);
                } else {
                    renderSingleChips(opts, msg.id);
                }
            }

            // Property cards (experimental): rich cards with image
            // strip + key details, rendered when the brain attaches
            // property_cards to content_attributes. Falls back to the
            // plain property_links pill row when cards aren't present
            // (e.g. older bot replies, or message types the brain
            // hasn't enriched). Cards already carry their own click
            // affordance (open URL in new tab) so we don't render the
            // pill row redundantly when cards are present.
            const propCards = extractMessagePropertyCards(msg);
            if (propCards.length > 0) {
                renderPropertyCards(propCards);
            } else {
                const propLinks = extractMessagePropertyLinks(msg);
                if (propLinks.length > 0) {
                    renderPropertyLinks(propLinks);
                }
            }

            // Structured numeric/email/tel/year input: when the brain
            // emits a [INPUT:type=...] marker, render a real typed input
            // beneath the bubble. This bypasses Gemini's tendency to
            // garble short digit replies — the LLM never sees a free-text
            // numeric token, only the structured value that came back.
            const inputSpec = extractMessageInput(msg);
            if (inputSpec) {
                renderInlineInput(inputSpec, msg.id, focus);
            }
        }

        scrollToEnd();
    }

    function setTyping(on: boolean): void {
        let typing = messages.querySelector<HTMLDivElement>(
            ".amai-chat-typing",
        );
        if (on && !typing) {
            typing = document.createElement("div");
            typing.className = "amai-chat-bubble amai-chat-bubble-bot amai-chat-typing";
            typing.innerHTML = "<span></span><span></span><span></span>";
            messages.appendChild(typing);
            scrollToEnd();
        } else if (!on && typing) {
            typing.remove();
        }
    }

    /** Inside the list only: the page itself never scrolls along. */
    function scrollToEnd(): void {
        messages.scrollTop = messages.scrollHeight;
        // A hidden list (the bubble's popover while it is closed) has no
        // scroll position to set; revealed() sets it once it shows.
        if (messages.clientHeight === 0) scrollPending = true;
    }

    function revealed(): void {
        // Still not drawn: keep it for when it is.
        if (!scrollPending || messages.clientHeight === 0) return;
        scrollPending = false;
        messages.scrollTop = messages.scrollHeight;
    }

    function setLive(on: boolean): void {
        messages.setAttribute("aria-live", on ? "polite" : "off");
    }

    inputRow.addEventListener("submit", (e) => {
        e.preventDefault();
        const text = inputField.value;
        inputField.value = "";
        params.onSend(text);
    });

    return {
        inputField,
        show,
        setTyping,
        renderOptions,
        answered,
        revealed,
        setLive,
        galleryOpen: () => activeGallery !== undefined,
        closeGallery,
    };
}

export const CHAT_ICON = `
<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
</svg>
`.trim();

/** Paper-plane send glyph. Solid rather than stroked: an outline of the
 *  same box reads visibly smaller and thinner against the filled round
 *  button. Fills its 24×24 viewBox edge to edge so the rendered size is
 *  the size it looks. Replaces a bare "→". */
const SEND_ICON = `
<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
  <path d="M1.5 21.5 23 12 1.5 2.5l.01 7.4L17 12 1.51 14.1z"></path>
</svg>
`.trim();

interface InlineInputSpec {
    type: "number" | "year" | "email" | "tel" | "text";
    placeholder?: string;
    min?: number;
    max?: number;
    autocomplete?: "be-address";
    skippable?: boolean;
    skip_label?: string;
    skip_value?: string;
}

interface OptionsMeta {
    multi?: boolean;
    confirmLabel?: string;
    /** Chip values that are mutually exclusive with all the others — when
     *  one is selected, the others are auto-unselected (and vice versa). */
    exclusiveValues?: string[];
}

function extractOptionsMeta(msg: ChatMessage): OptionsMeta {
    const attrs = msg.content_attributes;
    if (!attrs || typeof attrs !== "object") return {};
    const a = attrs as Record<string, unknown>;
    const meta: OptionsMeta = {};
    if (a.multi === true) meta.multi = true;
    if (typeof a.confirm_label === "string") meta.confirmLabel = a.confirm_label;
    if (Array.isArray(a.exclusive_values)) {
        meta.exclusiveValues = a.exclusive_values.filter(
            (v): v is string => typeof v === "string",
        );
    }
    return meta;
}

function extractMessageInput(msg: ChatMessage): InlineInputSpec | undefined {
    const attrs = msg.content_attributes;
    if (!attrs || typeof attrs !== "object") return undefined;
    const raw = (attrs as Record<string, unknown>).input;
    if (!raw || typeof raw !== "object") return undefined;
    const r = raw as Record<string, unknown>;
    const allowed: InlineInputSpec["type"][] = [
        "number",
        "year",
        "email",
        "tel",
        "text",
    ];
    const t = typeof r.type === "string" ? r.type : "text";
    const type = (allowed as string[]).includes(t)
        ? (t as InlineInputSpec["type"])
        : "text";
    const spec: InlineInputSpec = { type };
    if (typeof r.placeholder === "string") spec.placeholder = r.placeholder;
    if (typeof r.min === "number") spec.min = r.min;
    if (typeof r.max === "number") spec.max = r.max;
    if (r.autocomplete === "be-address") spec.autocomplete = "be-address";
    if (r.skippable === true) {
        spec.skippable = true;
        if (typeof r.skip_label === "string") spec.skip_label = r.skip_label;
        if (typeof r.skip_value === "string") spec.skip_value = r.skip_value;
    }
    return spec;
}

function generateSessionToken(): string {
    // 32 random hex chars. Doesn't need to be cryptographically strong —
    // it's a Google Places session token, opaque to us and only used so
    // a series of autocomplete calls gets billed once.
    const arr = new Uint8Array(16);
    (globalThis.crypto || (window as { crypto?: Crypto }).crypto)?.getRandomValues(arr);
    return Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("");
}

interface PropertyCard {
    id: string;
    url: string;
    label: string;
    name: string;
    type?: string;
    price?: { amount: number; currency: string };
    bedrooms?: number;
    bathrooms?: number;
    area_m2?: number;
    city?: string;
    postcode?: string;
    images: { url: string }[];
}

function extractMessagePropertyCards(msg: ChatMessage): PropertyCard[] {
    const attrs = msg.content_attributes;
    if (!attrs || typeof attrs !== "object") return [];
    const raw = (attrs as Record<string, unknown>).property_cards;
    if (!Array.isArray(raw)) return [];
    const out: PropertyCard[] = [];
    for (const item of raw) {
        if (!item || typeof item !== "object") continue;
        const r = item as Record<string, unknown>;
        const url = typeof r.url === "string" ? r.url : "";
        if (!/^https?:\/\//i.test(url)) continue;
        const id = typeof r.id === "string" ? r.id : "";
        const label = typeof r.label === "string" ? r.label : "";
        const name = typeof r.name === "string" ? r.name : "";
        const images: { url: string }[] = [];
        if (Array.isArray(r.images)) {
            for (const imgRaw of r.images) {
                if (!imgRaw || typeof imgRaw !== "object") continue;
                const u = (imgRaw as Record<string, unknown>).url;
                if (typeof u === "string" && /^https?:\/\//i.test(u)) {
                    images.push({ url: u });
                }
            }
        }
        const card: PropertyCard = { id, url, label, name, images };
        if (typeof r.type === "string") card.type = r.type;
        if (r.price && typeof r.price === "object") {
            const p = r.price as Record<string, unknown>;
            if (typeof p.amount === "number" && Number.isFinite(p.amount)) {
                card.price = {
                    amount: p.amount,
                    currency:
                        typeof p.currency === "string" ? p.currency : "EUR",
                };
            }
        }
        if (typeof r.bedrooms === "number") card.bedrooms = r.bedrooms;
        if (typeof r.bathrooms === "number") card.bathrooms = r.bathrooms;
        if (typeof r.area_m2 === "number") card.area_m2 = r.area_m2;
        if (typeof r.city === "string") card.city = r.city;
        if (typeof r.postcode === "string") card.postcode = r.postcode;
        out.push(card);
    }
    return out;
}

function extractMessagePropertyLinks(
    msg: ChatMessage,
): { title: string; url: string }[] {
    const attrs = msg.content_attributes;
    if (!attrs || typeof attrs !== "object") return [];
    const raw = (attrs as Record<string, unknown>).property_links;
    if (!Array.isArray(raw)) return [];
    const out: { title: string; url: string }[] = [];
    for (const item of raw) {
        if (!item || typeof item !== "object") continue;
        const o = item as { title?: unknown; url?: unknown };
        if (typeof o.title !== "string" || typeof o.url !== "string") continue;
        // Defence: only accept http/https URLs at the widget layer too.
        if (!/^https?:\/\//i.test(o.url)) continue;
        out.push({ title: o.title, url: o.url });
    }
    return out;
}

function extractMessageOptions(
    msg: ChatMessage,
): { title: string; value: string }[] {
    const attrs = msg.content_attributes;
    if (!attrs || typeof attrs !== "object") return [];
    const raw = (attrs as Record<string, unknown>).options;
    if (!Array.isArray(raw)) return [];
    const out: { title: string; value: string }[] = [];
    for (const item of raw) {
        if (!item || typeof item !== "object") continue;
        const o = item as { title?: unknown; value?: unknown };
        if (typeof o.title !== "string") continue;
        out.push({
            title: o.title,
            value: typeof o.value === "string" ? o.value : o.title,
        });
    }
    return out;
}

export function escapeHtml(s: string): string {
    return s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}


/**
 * Render a chat message body as safe HTML. Supports:
 *   - markdown links: [text](https://example.com)
 *   - bare URLs (http/https/www.)
 *   - simple emphasis: **bold**, *italic* (only for bot/agent replies via
 *     markdown is overkill; we keep it minimal to avoid CSS surprises)
 * Everything else is escaped. We tokenize first, then reassemble — so an
 * attacker can't inject HTML through link text.
 */
function renderRichText(input: string): string {
    if (!input) return "";

    // Replace markdown list markers at the start of each line with a
    // real bullet glyph. Matches `* ` or `- ` after a newline or at
    // the very start of the text. Two-asterisk runs (`**bold**`)
    // aren't touched because the regex requires whitespace after the
    // marker.
    input = input.replace(/(^|\n)[*\-]\s+/g, "$1• ");

    const tokens: Array<{ kind: "text" | "link"; text: string; href?: string }> = [];
    // Accepted link targets: absolute http(s)/mailto/tel, plus
    // site-relative paths ("/waardebepaling") — the bot's knowledge
    // bundle is scraped from the agency site and the LLM sometimes
    // reproduces its internal links relative. The widget lives in the
    // host page's DOM, so a relative href resolves against the agency
    // site naturally. `(?![\/\\])` blocks protocol-relative
    // "//evil.com" AND "/\evil.com" — browsers normalize a backslash
    // to a slash when resolving URLs, so both would navigate off-site.
    const mdLink = /\[([^\]]+)\]\((https?:\/\/[^\s)]+|mailto:[^\s)]+|tel:[^\s)]+|\/(?![/\\])[^\s)]*)\)/g;
    const bareUrl = /(https?:\/\/[^\s<]+[^\s<.,;:!?)\]'"]|www\.[^\s<]+[^\s<.,;:!?)\]'"])/g;

    // Pass 1: pull out markdown-style links so their text isn't re-linkified
    // by the bare-URL pass.
    let cursor = 0;
    let m: RegExpExecArray | null;
    mdLink.lastIndex = 0;
    while ((m = mdLink.exec(input)) !== null) {
        if (m.index > cursor) {
            tokens.push({ kind: "text", text: input.slice(cursor, m.index) });
        }
        tokens.push({ kind: "link", text: m[1], href: m[2] });
        cursor = m.index + m[0].length;
    }
    if (cursor < input.length) {
        tokens.push({ kind: "text", text: input.slice(cursor) });
    }

    // Pass 2: in each text token, linkify bare URLs.
    const expanded: typeof tokens = [];
    for (const t of tokens) {
        if (t.kind === "link") {
            expanded.push(t);
            continue;
        }
        let lastEnd = 0;
        let bm: RegExpExecArray | null;
        bareUrl.lastIndex = 0;
        while ((bm = bareUrl.exec(t.text)) !== null) {
            if (bm.index > lastEnd) {
                expanded.push({ kind: "text", text: t.text.slice(lastEnd, bm.index) });
            }
            const raw = bm[1];
            const href = raw.startsWith("www.") ? `https://${raw}` : raw;
            expanded.push({ kind: "link", text: raw, href });
            lastEnd = bm.index + bm[0].length;
        }
        if (lastEnd < t.text.length) {
            expanded.push({ kind: "text", text: t.text.slice(lastEnd) });
        }
    }

    return expanded
        .map((t) => {
            if (t.kind === "link" && t.href) {
                return `<a href="${escapeHtml(t.href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(t.text)}</a>`;
            }
            // Render **bold** inside plain text tokens. Done after
            // escaping so the asterisks (literal text from the LLM)
            // never become HTML — we wrap escaped chunks with <strong>
            // tags manually. Non-greedy match, no nesting.
            return renderBoldInEscapedText(escapeHtml(t.text));
        })
        .join("");
}

function renderBoldInEscapedText(escaped: string): string {
    // Match **…** where the inner content has no asterisks (so we
    // never accidentally span across two separate bold runs).
    return escaped.replace(/\*\*([^*\n]+?)\*\*/g, "<strong>$1</strong>");
}
